<script setup>
import { useForm, usePage } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance, toRaw, reactive, onMounted, watch } from 'vue';
import axios from 'axios';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import AutoComplete from 'primevue/autocomplete';
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Divider from 'primevue/divider';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import FloatLabel from 'primevue/floatlabel';
import InputNumber from 'primevue/inputnumber';
import Checkbox from 'primevue/checkbox';
import Tag from 'primevue/tag';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import ClientEmailMessagePanel from '@/components/admin/ClientEmailMessagePanel.vue';
import BudgetReviewChangesDialog from './BudgetReviewChangesDialog.vue';
import BudgetSuggestionsPanel from './BudgetSuggestionsPanel.vue';
import { useLoading } from '@/composables/useLoading';
import QuickOfferModal from '@/components/QuickOfferModal.vue';
import Message from 'primevue/message';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const editingCurrency = ref('USD');

defineOptions({
    layout: AdminMainLayout,
});

const { formatWithSymbol, formatConvertedPrice, getCurrentCurrency, onCurrencyChange, convertPrice } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const { coins, clients, types, sellers, editBudget, sites: sitesProp, bestSellers, recommended } = defineProps({
    coins: Object,
    clients: Array,
    types: Array,
    sellers: Array,
    editBudget: Object,
    sites: Object,
    bestSellers: { type: Array, default: () => [] },
    recommended: { type: Array, default: () => [] },
});

const form = useForm({
    client_id: null,
    mode: 'budget',
    sites: [],
});

const budgetEmailDialogVisible = ref(false);
const budgetEmailAddress = ref('');
const budgetProcessing = ref(false);
const sendToMyself = ref(false);
const sendToOther = ref(false);
const sendToClient = ref(false);
const budgetEmailLocale = ref('en');
const budgetEmailOptions = ref({ message: null, locale: null });
const emailError = ref('');
const localeOptions = [
    { label: 'English', value: 'en' },
    { label: 'Español', value: 'es' },
    { label: 'Português', value: 'pt' },
];

onMounted(() => {
    // If editing a saved budget, load it
    if (editBudget) {
        form.client_id = editBudget.client_id;

        if (editBudget.items && editBudget.items.length > 0) {
            const processedSites = editBudget.processedSites || {};
            form.sites = editBudget.items.map(item => {
                const siteUrl = item.site?.url;
                const processedSite = siteUrl ? processedSites[siteUrl] : null;

                const currentType = processedSite?.types?.find(t => t.id === item.type_id);
                const currentSale = currentType?.converted_price ?? null;
                const currentSaleCoin = currentType?.converted_coin || item.sale_coin || 'BRL';
                const currentCost = currentType?.cost ?? null;
                const currentCostCoin = currentType?.cost_coin || item.cost_coin || 'BRL';
                const savedCost = item.cost || 0;
                const sameCostCoin = currentCostCoin === (item.cost_coin || 'BRL');
                const costChanged = !!(currentType && savedCost > 0 && currentCost !== null &&
                    sameCostCoin && Math.abs(currentCost - savedCost) / Math.max(savedCost, 1) > 0.01);
                const costUp = currentCost !== null && currentCost > savedCost;
                const priceChanged = costChanged;
                const saleChanged = false;

                return {
                    id: item.site?.id,
                    url: item.site?.url || '-',
                    da: item.site?.da || 0,
                    dr: item.site?.dr || 0,
                    traffic: item.site?.traffic || 0,
                    branded_content: item.site?.branded_content ?? false,
                    link_type: processedSite?.link_type || item.site?.link_type || 'DOFOLLOW',
                    type_id: item.type_id,
                    seller_id: item.seller_id,
                    seller: item.seller,
                    cost: item.cost || 0,
                    cost_coin: item.cost_coin || 'BRL',
                    sale: item.sale || 0,
                    sale_coin: item.sale_coin || 'BRL',
                    payment_tax: 0,
                    priceChanged: priceChanged,
                    saleChanged: saleChanged,
                    costChanged: costChanged,
                    costUp: costUp,
                    currentSale: currentSale,
                    currentSaleCoin: currentSaleCoin,
                    currentCost: currentCost,
                    currentCostCoin: currentCostCoin,
                    types: processedSite?.types || item.site?.offers?.map(o => ({
                        id: o.type_id,
                        name: o.type?.name,
                        converted_price: o.sale,
                        converted_coin: o.sale_coin,
                        cost: o.cost,
                        cost_coin: o.cost_coin,
                        seller: o.seller,
                        seller_id: o.seller_id,
                    })) || [],
                    offers: processedSite?.offers || [],
                    sellers: processedSite?.sellers || [],
                    link: '',
                    observations: '',
                };
            });
        }

        // Snapshot original state for dirty tracking and change preview
        originalSnapshot.value = {
            client_id: form.client_id,
            sites: JSON.parse(JSON.stringify(form.sites.map(s => ({
                id: s.id,
                url: s.url,
                seller_id: s.seller_id,
                seller: s.seller,
                type_id: s.type_id,
                cost: s.cost,
                cost_coin: s.cost_coin,
                sale: s.sale,
                sale_coin: s.sale_coin,
                types: s.types,
            })))),
        };
    }
});

const originalSnapshot = ref(null);
const savePreviewDialogVisible = ref(false);
const changesPreview = ref({ clientChange: null, siteRows: [] });

const displayEqual = (valA, coinA, valB, coinB) => {
    return formatConvertedPrice(valA, coinA || 'BRL') === formatConvertedPrice(valB, coinB || 'BRL');
};

const isDirtyEdit = computed(() => {
    if (!editBudget) return true;
    if (hasAppliedPriceUpdates.value) return true;
    if (!originalSnapshot.value) return false;
    const snap = originalSnapshot.value;
    if (form.client_id !== snap.client_id) return true;
    if (form.sites.length !== snap.sites.length) return true;
    for (let i = 0; i < form.sites.length; i++) {
        const curr = form.sites[i];
        const orig = snap.sites[i];
        if (!orig) return true;
        if (curr.seller_id !== orig.seller_id) return true;
        if (curr.type_id !== orig.type_id) return true;
        if (!displayEqual(curr.cost, curr.cost_coin, orig.cost, orig.cost_coin)) return true;
        if (!displayEqual(curr.sale, curr.sale_coin, orig.sale, orig.sale_coin)) return true;
    }
    return false;
});

const buildChangesPreview = () => {
    const snap = originalSnapshot.value;
    if (!snap) return { clientChange: null, siteRows: [] };

    let clientChange = null;
    if (form.client_id !== snap.client_id) {
        const oldClient = clientOptions.value.find(c => c.value === snap.client_id);
        const newClient = clientOptions.value.find(c => c.value === form.client_id);
        clientChange = { oldValue: oldClient?.label || '-', newValue: newClient?.label || '-' };
    }

    const siteRows = [];
    const maxLen = Math.max(form.sites.length, snap.sites.length);
    for (let i = 0; i < maxLen; i++) {
        const curr = form.sites[i];
        const orig = snap.sites[i];
        const sellerChanged = curr?.seller_id !== orig?.seller_id;
        const typeChanged = curr?.type_id !== orig?.type_id;
        const costChanged = !displayEqual(curr?.cost, curr?.cost_coin, orig?.cost, orig?.cost_coin);
        const saleChanged = !displayEqual(curr?.sale, curr?.sale_coin, orig?.sale, orig?.sale_coin);
        const added = !orig && !!curr;
        const removed = !!orig && !curr;
        if (sellerChanged || typeChanged || costChanged || saleChanged || added || removed) {
            const oldType = (orig?.types || []).find(t => t.id === orig?.type_id);
            const newType = (curr?.types || []).find(t => t.id === curr?.type_id);
            siteRows.push({
                url: curr?.url || orig?.url || '-',
                added, removed,
                sellerChanged, sellerOld: orig?.seller?.name || '-', sellerNew: curr?.seller?.name || '-',
                typeChanged, typeOld: oldType?.name || '-', typeNew: newType?.name || '-',
                costChanged,
                costOld: formatConvertedPrice(orig?.cost, orig?.cost_coin || 'BRL'),
                costNew: formatConvertedPrice(curr?.cost, curr?.cost_coin || 'BRL'),
                saleChanged,
                saleOld: formatConvertedPrice(orig?.sale, orig?.sale_coin || 'BRL'),
                saleNew: formatConvertedPrice(curr?.sale, curr?.sale_coin || 'BRL'),
            });
        }
    }
    return { clientChange, siteRows };
};

const handleSave = () => {
    if (!isFormValid.value) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Please select a type for all sites'), life: 3000 });
        return;
    }
    if (editBudget?.budget_number) {
        changesPreview.value = buildChangesPreview();
        savePreviewDialogVisible.value = true;
        return;
    }
    submit();
};

const confirmSave = () => {
    savePreviewDialogVisible.value = false;
    submit();
};

const submit = () => {
    if (!isFormValid.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a type for all sites'),
            life: 3000
        });
        return;
    }

    const payload = {
        client_id: form.client_id,
        selected_currency: currentCurrency.value,
        items: form.sites.map((item) => ({
            site_id: item.is_manual ? null : item.id,
            manual_url: item.is_manual ? (item.manual_url || item.url) : null,
            seller_id: item.seller_id || item.seller?.id,
            type_id: item.type_id || null,
            cost: item.cost,
            sale: item.sale,
            cost_coin: item.cost_coin || currentCurrency.value,
            sale_coin: item.sale_coin || currentCurrency.value,
            is_manual: item.is_manual || false,
        })),
    };

    const isEditing = !!editBudget?.budget_number;

    if (isEditing) {
        form.transform(() => payload).put(route('budgets.update', editBudget.budget_number), {
            onStart: () => showLoading($t('Updating budget...')),
            onFinish: () => hideLoading(),
            onError() {
                toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to update budget'), life: 3000 });
            },
            onSuccess() {
                toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Budget updated successfully'), life: 3000 });
                window.location.href = route('budgets.index');
            }
        });
    } else {
        form.transform((data) => ({
            ...payload,
            mode: 'budget',
        })).post(route('orders.store'), {
            onStart: () => showLoading($t('Saving budget...')),
            onFinish: () => hideLoading(),
            onError() {
                toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to save budget'), life: 3000 });
            },
            onSuccess() {
                toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Budget saved successfully'), life: 3000 });
                window.location.href = route('budgets.index');
            }
        });
    }
};

const listForm = useForm({
    list: '',
});

const list = ref({});

const selectedAddNicho = ref(null);

const nichoTypes = computed(() => {
    return (types || []).map(t => ({ label: t.name, value: t.id }));
});

const selectBestOffer = (site, preferredTypeId = null) => {
    if (!site.offers?.length) return null;

    if (preferredTypeId) {
        const preferredOffer = site.offers.find(o => o.id === preferredTypeId);
        if (preferredOffer) return preferredOffer;
    }

    const isGambling = site.gambling === true;

    if (isGambling) {
        const nicheOffer = site.offers.find(o =>
            o.seller?.name?.toLowerCase().includes('nicho') ||
            o.seller?.name?.toLowerCase().includes('bet')
        );
        if (nicheOffer) return nicheOffer;
    }

    const linkarmeOffer = site.offers.find(o =>
        o.seller?.linkarme_seller === true ||
        o.seller?.name === 'Linkarme'
    );
    if (linkarmeOffer) return linkarmeOffer;

    return site.offers.reduce((min, o) => {
        const minPrice = min.converted_price || min.cost || 0;
        const oPrice = o.converted_price || o.cost || 0;
        return oPrice < minPrice ? o : min;
    });
};

// Budget actions
const downloadBudgetPdf = () => {
    if (form.sites.length === 0) return;

    budgetProcessing.value = true;

    const sitesData = form.sites.map(site => {
        const selectedType = site.types?.find(t => t.id === site.type_id);
        const convertedSale = Math.round(convertPrice(site.sale, site.sale_coin || 'USD', currentCurrency.value) * 100);
        return {
            url: site.url,
            da: Math.round(site.da || 0),
            dr: Math.round(site.dr || 0),
            traffic: Math.round(site.traffic || 0),
            type_name: selectedType?.name || '-',
            link_type: formatLinkType(site.link_type),
            type_observation: site.type_observation || null,
            branded_content: site.branded_content ?? false,
            sale: convertedSale || 0,
            sale_coin: currentCurrency.value,
            seller_name: site.seller?.name || '-',
        };
    });

    axios.post(route('orders.budget.download'), {
        sites: sitesData,
        currency: currentCurrency.value,
        budget_number: editBudget?.budget_number || null,
        locale: usePage().props.locale || 'en',
    }, { responseType: 'blob' })
    .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `orcamento-${new Date().toISOString().slice(0,10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    })
    .catch(async (error) => {
        let detail = $t('Failed to download PDF');
        if (error.response?.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const json = JSON.parse(text);
                detail = json.message || Object.values(json.errors || {}).flat().join(', ') || detail;
            } catch (_) {}
        }
        toast.add({ severity: 'error', summary: $t('Error'), detail, life: 5000 });
    })
    .finally(() => { budgetProcessing.value = false; });
};

const previewBudgetPdf = () => {
    if (form.sites.length === 0) return;

    budgetProcessing.value = true;

    const sitesData = form.sites.map(site => {
        const selectedType = site.types?.find(t => t.id === site.type_id);
        const convertedSale = Math.round(convertPrice(site.sale, site.sale_coin || 'USD', currentCurrency.value) * 100);
        return {
            url: site.url,
            da: Math.round(site.da || 0),
            dr: Math.round(site.dr || 0),
            traffic: Math.round(site.traffic || 0),
            type_name: selectedType?.name || '-',
            link_type: formatLinkType(site.link_type),
            type_observation: site.type_observation || null,
            branded_content: site.branded_content ?? false,
            sale: convertedSale || 0,
            sale_coin: currentCurrency.value,
            seller_name: site.seller?.name || '-',
        };
    });

    axios.post(route('orders.budget.download'), {
        sites: sitesData,
        currency: currentCurrency.value,
        budget_number: editBudget?.budget_number || null,
        locale: usePage().props.locale || 'en',
    }, { responseType: 'blob' })
    .then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    })
    .catch(async (error) => {
        let detail = $t('Failed to preview PDF');
        if (error.response?.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const json = JSON.parse(text);
                detail = json.message || Object.values(json.errors || {}).flat().join(', ') || detail;
            } catch (_) {}
        }
        toast.add({ severity: 'error', summary: $t('Error'), detail, life: 5000 });
    })
    .finally(() => { budgetProcessing.value = false; });
};

const sendBudgetEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    emailError.value = '';

    if (sendToOther.value) {
        if (!budgetEmailAddress.value) {
            emailError.value = $t('Email is required');
            return;
        }
        if (!emailRegex.test(budgetEmailAddress.value)) {
            emailError.value = $t('Please enter a valid email address');
            return;
        }
    }

    const emails = [];
    const adminLocale = usePage().props.locale || 'en';

    if (sendToMyself.value) {
        const myEmail = usePage().props.auth?.user?.email;
        if (myEmail) emails.push({ email: myEmail, locale: adminLocale });
    }
    if (sendToClient.value && selectedClient.value?.client?.email) {
        const clientLocale = selectedClient.value.client.locale || 'en';
        emails.push({ email: selectedClient.value.client.email, locale: clientLocale });
    }
    if (sendToOther.value && budgetEmailAddress.value) {
        emails.push({ email: budgetEmailAddress.value, locale: budgetEmailLocale.value });
    }
    if (emails.length === 0 || form.sites.length === 0) return;

    budgetProcessing.value = true;

    const sitesData = form.sites.map(site => {
        const selectedType = site.types?.find(t => t.id === site.type_id);
        const convertedSale = Math.round(convertPrice(site.sale, site.sale_coin || 'USD', currentCurrency.value) * 100);
        return {
            url: site.url,
            da: Math.round(site.da || 0),
            dr: Math.round(site.dr || 0),
            traffic: Math.round(site.traffic || 0),
            type_name: selectedType?.name || '-',
            link_type: formatLinkType(site.link_type),
            type_observation: site.type_observation || null,
            branded_content: site.branded_content ?? false,
            sale: convertedSale || 0,
            sale_coin: currentCurrency.value,
            seller_name: site.seller?.name || '-',
        };
    });

    axios.post(route('orders.budget.email'), {
        recipients: emails,
        sites: sitesData,
        currency: currentCurrency.value,
        budget_number: editBudget?.budget_number || null,
        client_message: budgetEmailOptions.value.message,
    })
    .then(() => {
        budgetEmailDialogVisible.value = false;
        budgetEmailAddress.value = '';
        sendToMyself.value = false;
        sendToClient.value = false;
        sendToOther.value = false;
        budgetEmailLocale.value = 'en';
        emailError.value = '';
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Budget sent by email'), life: 3000 });
    })
    .catch(() => {
        toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to send budget email'), life: 3000 });
    })
    .finally(() => { budgetProcessing.value = false; });
};

const importBudgetToOrder = () => {
    // Navigate to orders create page loading the same sites
    const siteIds = form.sites.map(s => s.id).filter(Boolean);
    // Simply redirect to orders create — the user will re-add sites there
    // For now, show a message
    toast.add({ severity: 'info', summary: $t('Info'), detail: $t('Redirecting to order creation...'), life: 2000 });

    // If editing a budget, navigate with budget number so OrdersCreate can load it
    if (editBudget?.budget_number) {
        window.location.href = route('orders.create') + `?editBudgetNumber=${editBudget.budget_number}&mode=order`;
    } else {
        // For unsaved budgets, just navigate to orders create
        window.location.href = route('orders.create');
    }
};

const normalizeUrl = (url) => {
    try {
        if (!url || !url.trim()) return '';
        let cleanUrl = url.trim();
        cleanUrl = cleanUrl.replace(/^https?:\/\//i, '');
        cleanUrl = cleanUrl.replace(/^www\./i, '');
        cleanUrl = cleanUrl.replace(/\/+$/, '');
        const queryIndex = cleanUrl.indexOf('?');
        if (queryIndex !== -1) cleanUrl = cleanUrl.substring(0, queryIndex);
        return cleanUrl.toLowerCase();
    } catch (e) {
        return url.trim();
    }
};

const go = () => {
    if (!listForm.list || !listForm.list.trim()) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Please enter at least one site URL'), life: 3000 });
        return;
    }

    let processedList = listForm.list
        .split('\n')
        .map(url => normalizeUrl(url))
        .filter(url => url.length > 0)
        .join('\n');

    const originalList = listForm.list;
    listForm.list = processedList;

    listForm.transform(() => ({
        list: processedList,
        currency: currentCurrency.value,
    })).post(route('orders.go'), {
        preserveScroll: true,
        preserveState: true,
        onStart: () => showLoading('Processing list...'),
        onFinish: () => hideLoading(),
        onSuccess: (res) => {
            openOrderDialog.value = false;
            list.value = {};
            notFoundItems.value = [];

            if (res.props?.flash?.notFound) {
                notFoundItems.value = res.props.flash.notFound || [];
            }

            if (res.props?.flash?.sites) {
                const _sites = res.props.flash.sites;

                if (_sites && Object.keys(_sites).length > 0) {
                    const siteCounts = {};
                    const siteUrls = processedList.split('\n').filter(url => url.trim());

                    siteUrls.forEach(url => {
                        siteCounts[url] = (siteCounts[url] || 0) + 1;
                    });

                    Object.keys(_sites).forEach(url => {
                        const site = _sites[url];
                        const count = siteCounts[url] || 1;

                        for (let i = 0; i < count; i++) {
                            const siteKey = count > 1 ? `${url}_${i + 1}` : url;
                            const siteEntry = { ...site };
                            siteEntry.offer = selectBestOffer(site, selectedAddNicho.value);
                            siteEntry.originalUrl = url;
                            siteEntry.instanceNumber = i + 1;
                            siteEntry.totalInstances = count;
                            list.value[siteKey] = siteEntry;
                        }
                    });
                }
            }

            // Show dialog if we have sites OR not found items
            if (Object.keys(list.value).length > 0 || notFoundItems.value.length > 0) {
                showNotFoundSection.value = true;
                openSitesDialog.value = true;
            } else {
                toast.add({ severity: 'info', summary: $t('Info'), detail: $t('No sites found in the list.'), life: 3000 });
            }
        },
        onError: (errors) => {
            listForm.list = originalList;
            let errorMessage = $t('Failed to process site list');
            if (errors.message) errorMessage = errors.message;
            else if (errors.list) errorMessage = errors.list[0] || errorMessage;
            toast.add({ severity: 'error', summary: $t('Error'), detail: errorMessage, life: 5000 });
        }
    });
};

const openOrderDialog = ref(false);
const openSitesDialog = ref(false);
const openEditDialog = ref(false);

// Not found items state
const notFoundItems = ref([]);
const showNotFoundSection = ref(true);

// Created sites state (from Quick Offer)
const createdSites = ref([]);

// Quick offer modal state
const quickOfferModalVisible = ref(false);
const quickOfferInitialUrl = ref('');
const quickOfferSiteExists = ref(false);
const quickOfferSiteId = ref(null);
const quickOfferSiteDa = ref(null);
const quickOfferSiteDr = ref(null);

const editingColumn = ref(null);
const editingRow = ref(null);
const editingSite = ref(null);
const editingValue = ref('');

const add = (site = null, index) => {
    if (site) {
        const newSite = JSON.parse(JSON.stringify(site));
        newSite.link = null;
        newSite.observations = '';
        newSite.types = newSite.types || [];
        newSite.sellers = newSite.sellers || [];

        newSite.cost = newSite.cost || 0;
        newSite.cost_coin = newSite.cost_coin || currentCurrency.value;
        newSite.sale = newSite.sale || 0;
        newSite.sale_coin = newSite.sale_coin || currentCurrency.value;
        newSite.payment_tax = 0;

        form.sites.splice(index + 1, 0, newSite);
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Site duplicated successfully'), life: 2000 });
        return;
    }

    const sitesAdded = Object.keys(list.value).length;
    if (sitesAdded === 0 && createdSites.value.length === 0) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('No sites available to add'), life: 3000 });
        return;
    }

    for (let key of Object.keys(list.value)) {
        const siteData = structuredClone(toRaw(list.value[key]));
        siteData.url = siteData.originalUrl || siteData.url;
        siteData.link = null;
        siteData.observations = '';
        siteData.types = siteData.types || [];
        siteData.sellers = siteData.sellers || [];
        siteData.link_type = siteData.link_type || 'DOFOLLOW';
        siteData.type_observation = siteData.type_observation || '';

        if (siteData.offer) {
            siteData.seller = siteData.offer.seller;
            siteData.seller_id = siteData.offer.seller_id;
            siteData.type_id = siteData.offer.id || null;
            siteData.cost = siteData.offer.cost || 0;
            siteData.cost_coin = siteData.offer.cost_coin || currentCurrency.value;
            siteData.sale = siteData.offer.converted_price || 0;
            siteData.sale_coin = siteData.offer.converted_coin || currentCurrency.value;
            siteData.payment_tax = 0;
        } else {
            siteData.cost = 0;
            siteData.sale = 0;
            siteData.sale_coin = currentCurrency.value;
            siteData.cost_coin = currentCurrency.value;
            siteData.seller_id = null;
            siteData.type_id = null;
            siteData.payment_tax = 0;
        }

        form.sites.push(siteData);
    }

    // Add items from quick offer createdSites
    createdSites.value.forEach(created => {
        (created._items || []).forEach(item => {
            form.sites.push(item);
        });
    });

    const totalAdded = sitesAdded + createdSites.value.reduce((sum, c) => sum + c.count, 0);
    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('{{count}} site(s) added', { count: totalAdded }), life: 3000 });
    openSitesDialog.value = false;
    list.value = {};
    listForm.list = '';
    createdSites.value = [];
};

const remove = (index) => {
    form.sites.splice(index, 1);
};

const edit = (column, row, site) => {
    editingColumn.value = column;
    editingRow.value = row;
    editingSite.value = site;
    editingCurrency.value = currentCurrency.value;
    const originalCurrency = site[`${column}_coin`] || 'USD';
    const originalValue = site[column] || 0;
    editingValue.value = convertPrice(originalValue, originalCurrency, currentCurrency.value);
    openEditDialog.value = true;
};

const update = (all) => {
    if (!editingValue.value || editingValue.value < 0) return;
    const column = editingColumn.value;

    if (all) {
        form.sites
            .filter(site => site.url === editingSite.value.url)
            .forEach(site => {
                const siteCurrency = site[`${column}_coin`] || 'USD';
                const convertedValue = convertPrice(editingValue.value * 100, currentCurrency.value, siteCurrency);
                site[column] = Math.floor(convertedValue * 100);
            });
    } else {
        const site = form.sites[editingRow.value];
        const siteCurrency = site[`${column}_coin`] || 'USD';
        const convertedValue = convertPrice(editingValue.value * 100, currentCurrency.value, siteCurrency);
        site[column] = Math.floor(convertedValue * 100);
    }

    openEditDialog.value = false;
    editingValue.value = '';
};

const showEditAll = computed(() => {
    if (editingSite.value === null) return false;
    return form.sites.filter(site => site.url === editingSite.value.url).length > 1;
});

const formatCurrency = (value, currencyCode) => {
    const currency = currencyCode || currentCurrency.value;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const costTotal = computed(() => {
    let total = 0;
    for (let site of form.sites) {
        if (site.type_id && site.cost) {
            const currency = site.cost_coin || 'USD';
            total += convertPrice(site.cost, currency, currentCurrency.value);
        }
    }
    return formatWithSymbol(total, currentCurrency.value);
});

const saleTotal = computed(() => {
    let total = 0;
    for (let site of form.sites) {
        if (site.sale) {
            const currency = site.sale_coin || 'USD';
            total += convertPrice(site.sale, currency, currentCurrency.value);
        }
    }
    return formatWithSymbol(total, currentCurrency.value);
});

const onTypeChange = (siteIndex) => {
    const site = form.sites[siteIndex];
    if (site.type_id) {
        const selectedOffer = site.types?.find(offer => offer.id === site.type_id);
        if (selectedOffer) {
            site.cost = selectedOffer.cost || 0;
            site.cost_coin = selectedOffer.cost_coin || currentCurrency.value;
            site.sale = selectedOffer.converted_price;
            site.sale_coin = selectedOffer.converted_coin;
            site.seller_id = selectedOffer.seller_id;
            site.seller = selectedOffer.seller;
        }
    } else {
        site.cost = 0;
        site.sale = 0;
        site.seller_id = null;
        site.seller = null;
    }
};

const isFormValid = computed(() => {
    if (form.sites.length === 0) return false;

    return form.sites.every(site => {
        // Manual items are valid if they have a sale price
        if (site.is_manual) {
            return site.sale > 0;
        }
        // Regular items need a type_id
        return site.type_id !== null && site.type_id !== undefined;
    });
});

const hasAnyPriceChange = computed(() => {
    return form.sites.some(site => site.priceChanged || site.costChanged);
});

const hasAppliedPriceUpdates = ref(false);

const applyCurrentPrice = (index, silent = false) => {
    const site = form.sites[index];
    if (!site.costChanged) return;
    site.cost = site.currentCost;
    site.cost_coin = site.currentCostCoin;
    if (site.currentSale !== null) {
        site.sale = site.currentSale;
        site.sale_coin = site.currentSaleCoin;
    }
    site.costChanged = false;
    site.priceChanged = false;
    hasAppliedPriceUpdates.value = true;
    if (!silent) {
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Price updated'), life: 2000 });
    }
};

const applyAllCurrentPrices = () => {
    form.sites.forEach((site, index) => {
        if (site.costChanged) {
            applyCurrentPrice(index, true);
        }
    });
    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('All prices updated'), life: 2000 });
};

const addSites = () => {
    openOrderDialog.value = true;
};

const clientOptions = computed(() => {
    return clients.map(client => {
        const fullName = [client.first_name, client.last_name]
            .filter(Boolean)
            .join(' ')
            .trim();
        return {
            label: fullName || client.name,
            value: client.id,
            client: client
        };
    });
});

const filteredClients = ref([]);
const selectedClient = ref(null);

watch(() => form.client_id, (newClientId) => {
    if (newClientId && !selectedClient.value) {
        const client = clientOptions.value.find(c => c.value === newClientId);
        if (client) selectedClient.value = client;
    }
}, { immediate: true });

const searchClients = (event) => {
    const query = event.query.toLowerCase();
    filteredClients.value = clientOptions.value.filter(client =>
        client.label.toLowerCase().includes(query)
    );
};

const onClientSelect = (event) => {
    const client = event.value;
    if (client && client.value) {
        form.client_id = client.value;
        selectedClient.value = client;
    }
};

const onClientClear = () => {
    form.client_id = null;
    selectedClient.value = null;
};

const getTypeOptions = (siteIndex) => {
    const site = form.sites[siteIndex];
    if (!site.types) return [];
    const uniqueTypes = new Map();
    site.types.forEach(type => {
        if (!uniqueTypes.has(type.id)) {
            const priceDisplay = formatConvertedPrice(type.converted_price, type.converted_coin);
            uniqueTypes.set(type.id, {
                label: `${type.name} - ${priceDisplay}`,
                value: type.id
            });
        }
    });
    return Array.from(uniqueTypes.values());
};

const formatLinkType = (linkType) => {
    if (!linkType) return 'NOFOLLOW';
    const normalizedType = linkType.toUpperCase().trim();
    if (normalizedType.includes('DOFOLLOW')) return 'DOFOLLOW';
    if (normalizedType.includes('SPONSOR')) return 'SPONSORED';
    return 'NOFOLLOW';
};

const linkTypeNotes = computed(() => form.sites.filter(site => site.type_observation));

/**
 * Get price indicator for a niche/type compared to other available niches
 * Returns: 'lowest' | 'highest' | null
 */
const getNichePriceIndicator = (types, typeId) => {
    if (!types || types.length <= 1 || !typeId) return null;

    const prices = types
        .filter(t => t.converted_price && t.converted_price > 0)
        .map(t => ({ id: t.id, price: t.converted_price }));

    if (prices.length <= 1) return null;

    const minPrice = Math.min(...prices.map(p => p.price));
    const maxPrice = Math.max(...prices.map(p => p.price));

    if (minPrice === maxPrice) return null;

    const currentType = prices.find(p => p.id === typeId);
    if (!currentType) return null;

    if (currentType.price === minPrice) return 'lowest';
    if (currentType.price === maxPrice) return 'highest';
    return null;
};

/**
 * Get price indicator for a seller compared to other sellers
 * Compares the lowest priced niche of each seller
 * Returns: 'lowest' | 'highest' | null
 */
const getSellerPriceIndicator = (types, sellerId) => {
    if (!types || !sellerId) return null;

    // Group types by seller and find lowest price for each
    const sellerLowestPrices = new Map();
    types.forEach(t => {
        if (!t.seller_id || !t.converted_price || t.converted_price <= 0) return;
        const current = sellerLowestPrices.get(t.seller_id);
        if (!current || t.converted_price < current) {
            sellerLowestPrices.set(t.seller_id, t.converted_price);
        }
    });

    if (sellerLowestPrices.size <= 1) return null;

    const prices = Array.from(sellerLowestPrices.values());
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) return null;

    const currentSellerPrice = sellerLowestPrices.get(sellerId);
    if (!currentSellerPrice) return null;

    if (currentSellerPrice === minPrice) return 'lowest';
    if (currentSellerPrice === maxPrice) return 'highest';
    return null;
};

/**
 * Get all niches for the selected seller with price indicators
 */
const getNichesForSeller = (types, sellerId) => {
    if (!types) return [];
    const filtered = sellerId ? types.filter(t => t.seller_id === sellerId) : types;
    return filtered;
};

const goBack = () => {
    window.location.href = route('budgets.index');
};

// ─── Suggestions ────────────────────────────────────────────────────────────
const clientHistory = ref([]);
const clientHistoryLoading = ref(false);

watch(() => form.client_id, async (newClientId) => {
    if (newClientId) {
        clientHistoryLoading.value = true;
        try {
            const res = await axios.get(route('budgets.suggestions'), {
                params: { client_id: newClientId, currency: currentCurrency.value },
            });
            clientHistory.value = res.data.history || [];
        } catch {
            clientHistory.value = [];
        } finally {
            clientHistoryLoading.value = false;
        }
    } else {
        clientHistory.value = [];
    }
});

const _pushSuggestion = (suggestion) => {
    const siteData = JSON.parse(JSON.stringify(suggestion));
    siteData.link = null;
    siteData.observations = '';
    siteData.types = siteData.types || [];
    siteData.sellers = siteData.sellers || [];
    siteData.link_type = siteData.link_type || 'DOFOLLOW';
    siteData.type_observation = siteData.type_observation || '';

    const bestOffer = selectBestOffer(siteData, null);
    if (bestOffer) {
        siteData.seller = bestOffer.seller;
        siteData.seller_id = bestOffer.seller_id;
        siteData.type_id = bestOffer.id || null;
        siteData.cost = bestOffer.cost || 0;
        siteData.cost_coin = bestOffer.cost_coin || currentCurrency.value;
        siteData.sale = bestOffer.converted_price || 0;
        siteData.sale_coin = bestOffer.converted_coin || currentCurrency.value;
    } else {
        siteData.cost = 0;
        siteData.sale = 0;
        siteData.seller_id = null;
        siteData.type_id = null;
    }
    siteData.payment_tax = 0;
    form.sites.push(siteData);
};

const addSuggestion = (suggestion) => {
    if (form.sites.some(s => s.id === suggestion.id)) {
        toast.add({ severity: 'info', summary: $t('Info'), detail: $t('Site already in budget'), life: 2000 });
        return;
    }
    _pushSuggestion(suggestion);
    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Site added to budget'), life: 2000 });
};

const addAllSuggestions = (sites) => {
    if (!sites || sites.length === 0) return;
    let added = 0;
    sites.forEach(site => {
        if (!form.sites.some(s => s.id === site.id)) {
            _pushSuggestion(site);
            added++;
        }
    });
    if (added > 0) {
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('{{count}} site(s) added', { count: added }), life: 3000 });
    }
};


const getOfferSaleDisplay = (offer) => {
    if (!offer) return null;
    const price = offer.converted_price || offer.calculated_price || offer.sale;
    if (!price || price <= 0) return null;
    const coin = offer.converted_price ? offer.converted_coin
        : offer.calculated_price ? offer.cost_coin
        : offer.sale_coin;
    return { price, coin };
};

onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});

// Open quick offer modal
const openQuickOfferModal = (item) => {
    quickOfferInitialUrl.value = item.normalized_url || item.original_url || '';
    quickOfferSiteExists.value = item.reason === 'no_valid_offers';
    quickOfferSiteId.value = item.site_id || null;
    quickOfferSiteDa.value = item.da || null;
    quickOfferSiteDr.value = item.dr || null;
    quickOfferModalVisible.value = true;
};

// Handle quick offer created - add directly to budget
const handleQuickOfferCreated = (data) => {
    const createdUrl = data.site?.url;
    if (createdUrl) {
        notFoundItems.value = notFoundItems.value.filter(
            item => item.normalized_url !== createdUrl && item.site_url !== createdUrl
        );
    }

    if (data.site && data.typesPayload && data.typesPayload.length > 0) {
        const addedNiches = [];
        const items = [];

        data.typesPayload.forEach(typePayload => {
            const typeForDropdown = {
                id: typePayload.type_id,
                name: typePayload.type_name,
                converted_price: typePayload.sale,
                converted_coin: typePayload.sale_coin,
                cost: typePayload.cost,
                cost_coin: typePayload.cost_coin,
                seller_id: data.seller?.id || null,
                seller: data.seller || null,
            };

            items.push({
                id: data.site.id,
                url: data.site.url,
                da: data.site.da || 0,
                dr: data.site.dr || 0,
                link_type: data.site.link_type || 'DOFOLLOW',
                seller_id: data.seller?.id || null,
                seller: data.seller || null,
                type_id: typePayload.type_id,
                cost: typePayload.cost,
                cost_coin: typePayload.cost_coin,
                sale: typePayload.sale,
                sale_coin: typePayload.sale_coin,
                payment_tax: 0,
                link: '',
                observations: '',
                types: [typeForDropdown],
                sellers: [data.seller],
            });

            addedNiches.push(typePayload.type_name);
        });

        createdSites.value.push({
            url: data.site.url,
            da: data.site.da || 0,
            dr: data.site.dr || 0,
            seller: data.seller?.name || '-',
            niches: addedNiches,
            count: data.typesPayload.length,
            _items: items,
        });

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Offer registered. Click "Add to order" to include it.'),
            life: 3000,
        });
    }
};

// Remove not found item from list
const removeNotFoundItem = (index) => {
    notFoundItems.value.splice(index, 1);
};
</script>

<template>

    <!-- Add Sites Dialog -->
    <Dialog v-model:visible="openOrderDialog" :modal="true" :style="{ width: '50vw' }" @hide="selectedAddNicho = null">
        <template #header>
            <div class="flex items-center justify-between w-full pr-2">
                <span class="font-bold text-xl">{{ $t('Add sites') }}</span>
                <Select
                    v-model="selectedAddNicho"
                    :options="nichoTypes"
                    option-label="label"
                    option-value="value"
                    :placeholder="$t('Select niche')"
                    class="w-48"
                    show-clear
                />
            </div>
        </template>
        <div class="field">
            <FloatLabel>
                <Textarea
                    id="list"
                    v-model="listForm.list"
                    rows="5"
                    class="w-full"
                    :placeholder="$t('Enter site URLs, one per line. Example:\nsite1.com\nsite2.com')"
                />
                <label for="list">{{ $t('Site list') }}</label>
            </FloatLabel>
            <small v-if="listForm.errors.list" class="p-error">{{ listForm.errors.list }}</small>
        </div>

        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openOrderDialog = false" />
            <Button :label="$t('Go')" icon="pi pi-check" @click="go" :disabled="listForm.processing" />
        </template>
    </Dialog>

    <!-- Sites Selection Dialog -->
    <Dialog v-model:visible="openSitesDialog" :header="$t('Add sites')" :modal="true" :style="{ width: '80vw', maxHeight: '90vh' }" :contentStyle="{ overflow: 'auto' }">
        <!-- Not Found Items Section (Red) -->
        <div v-if="notFoundItems.length > 0" class="mb-4">
            <div class="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
                <div
                    class="flex items-center justify-between p-3 bg-red-100 border-b border-red-200 cursor-pointer"
                    @click="showNotFoundSection = !showNotFoundSection"
                >
                    <div class="flex items-center gap-2">
                        <i class="pi pi-exclamation-circle text-red-600"></i>
                        <span class="font-semibold text-red-700">
                            {{ $t('Sites Not Found') }} ({{ notFoundItems.length }})
                        </span>
                    </div>
                    <i :class="showNotFoundSection ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-red-600"></i>
                </div>

                <div v-if="showNotFoundSection" class="p-3">
                    <DataTable
                        :value="notFoundItems"
                        responsiveLayout="scroll"
                        class="p-datatable-sm"
                    >
                        <Column :header="$t('URL')" style="min-width: 200px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.original_url || slotProps.data.normalized_url }}</span>
                            </template>
                        </Column>

                        <Column :header="$t('Reason')" style="min-width: 150px">
                            <template #body="slotProps">
                                <Tag
                                    :value="slotProps.data.reason === 'not_found' ? $t('Site not found') : $t('No offers')"
                                    :severity="slotProps.data.reason === 'not_found' ? 'danger' : 'warn'"
                                />
                            </template>
                        </Column>

                        <Column :header="$t('Actions')" style="min-width: 200px">
                            <template #body="slotProps">
                                <div class="flex gap-2">
                                    <Button
                                        :label="$t('Quick Offer')"
                                        icon="pi pi-plus"
                                        size="small"
                                        severity="success"
                                        @click="openQuickOfferModal(slotProps.data)"
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        size="small"
                                        severity="danger"
                                        text
                                        @click="removeNotFoundItem(slotProps.index)"
                                    />
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Created Sites Section (Blue) -->
        <div v-if="createdSites.length > 0" class="mb-4">
            <div class="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <div class="flex items-center justify-between p-3 bg-blue-100 border-b border-blue-200">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-check-circle text-blue-600"></i>
                        <span class="font-semibold text-blue-700">
                            {{ $t('Sites Created & Added') }} ({{ createdSites.length }})
                        </span>
                    </div>
                </div>

                <div class="p-3">
                    <DataTable
                        :value="createdSites"
                        responsiveLayout="scroll"
                        class="p-datatable-sm"
                    >
                        <Column :header="$t('URL')" style="min-width: 200px">
                            <template #body="slotProps">
                                <span class="text-sm font-medium">{{ slotProps.data.url }}</span>
                            </template>
                        </Column>

                        <Column header="DA" style="min-width: 60px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.da }}</span>
                            </template>
                        </Column>

                        <Column header="DR" style="min-width: 60px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.dr }}</span>
                            </template>
                        </Column>

                        <Column :header="$t('Seller')" style="min-width: 120px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.seller }}</span>
                            </template>
                        </Column>

                        <Column :header="$t('Niches')" style="min-width: 150px">
                            <template #body="slotProps">
                                <div class="flex flex-wrap gap-1">
                                    <Tag
                                        v-for="niche in slotProps.data.niches"
                                        :key="niche"
                                        :value="niche"
                                        severity="info"
                                        class="text-xs"
                                    />
                                </div>
                            </template>
                        </Column>

                        <Column :header="$t('Items')" style="min-width: 80px">
                            <template #body="slotProps">
                                <Tag :value="slotProps.data.count + ' ' + $t('items')" severity="success" />
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Found Sites Section (Green) -->
        <div v-if="Object.keys(list).length > 0" class="bg-green-50 border border-green-200 rounded-lg p-3">
            <div class="flex items-center gap-2 mb-3">
                <i class="pi pi-check-circle text-green-600"></i>
                <span class="font-semibold text-green-700">
                    {{ $t('Sites Found') }} ({{ Object.keys(list).length }})
                </span>
            </div>

            <DataTable :value="Object.entries(list).map(([key, site]) => ({key, url: site.originalUrl || site.url, ...site}))"
                       responsiveLayout="scroll"
                       class="p-datatable-sm">
                <Column :header="$t('Portal')">
                    <template #body="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.data.originalUrl || slotProps.data.url }}</span>
                            <span v-if="slotProps.data.totalInstances > 1" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                {{ slotProps.data.instanceNumber }}/{{ slotProps.data.totalInstances }}
                            </span>
                        </div>
                    </template>
                </Column>
                <Column field="offer.seller.name" :header="$t('Seller')">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.offer?.seller?.name ?? '-' }}</span>
                    </template>
                </Column>
                <Column :header="$t('Niche')">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.offer?.name ?? '-' }}</span>
                    </template>
                </Column>
                <Column :header="$t('Purchase Value')">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.offer === null">-</span>
                        <span v-else>
                            {{ formatConvertedPrice(slotProps.data.offer.cost, slotProps.data.offer.cost_coin) }}
                        </span>
                    </template>
                </Column>
                <Column :header="$t('Sale value')">
                    <template #body="slotProps">
                        <template v-for="saleInfo in [getOfferSaleDisplay(slotProps.data.offer)]" :key="0">
                            <span v-if="saleInfo">{{ formatConvertedPrice(saleInfo.price, saleInfo.coin) }}</span>
                            <span v-else>-</span>
                        </template>
                    </template>
                </Column>
            </DataTable>
        </div>

        <template #footer>
            <Button
                :label="$t('Edit List')"
                icon="pi pi-pencil"
                severity="secondary"
                text
                @click="openSitesDialog = false; openOrderDialog = true"
            />
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openSitesDialog = false" />
            <Button
                v-if="Object.keys(list).length > 0 || createdSites.length > 0"
                :label="$t('Add to order')"
                icon="pi pi-check"
                @click="add(null)"
            />
        </template>
    </Dialog>

    <!-- Edit Value Dialog -->
    <Dialog v-model:visible="openEditDialog" :header="$t('Edit value')" :modal="true" :style="{ width: '400px' }">
        <div class="field">
            <InputNumber
                v-model="editingValue"
                mode="currency"
                :currency="editingCurrency"
                locale="en-US"
                class="w-full"
                :min="0"
            />
        </div>
        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openEditDialog = false" />
            <Button v-if="showEditAll" :label="$t('Update all same site')" icon="pi pi-check-circle" severity="secondary" @click="update(true)" class="mr-2" />
            <Button :label="$t('Update')" icon="pi pi-check" @click="update(false)" />
        </template>
    </Dialog>

    <!-- Budget Email Dialog -->
    <Dialog v-model:visible="budgetEmailDialogVisible" :header="$t('Send Budget by Email')" :modal="true" :style="{ width: '450px' }">
        <div class="flex flex-col gap-4">
            <div class="flex items-center gap-2">
                <Checkbox v-model="sendToMyself" inputId="send_to_myself" :binary="true" />
                <label for="send_to_myself" class="cursor-pointer">{{ $t('Send to myself') }}</label>
            </div>
            <div v-if="selectedClient?.client?.email" class="flex items-center gap-2">
                <Checkbox v-model="sendToClient" inputId="send_to_client" :binary="true" />
                <label for="send_to_client" class="cursor-pointer">
                    {{ $t('Send to client') }} <span class="text-surface-500 text-sm">{{ selectedClient.client.email }}</span>
                </label>
            </div>
            <div class="flex items-center gap-2">
                <Checkbox v-model="sendToOther" inputId="send_to_other" :binary="true" />
                <label for="send_to_other" class="cursor-pointer">{{ $t('Send to another person') }}</label>
            </div>
            <div v-if="sendToOther" class="flex flex-col gap-1">
                <div class="flex gap-3 items-stretch">
                    <div class="flex-1">
                        <InputText
                            id="budget_email"
                            v-model="budgetEmailAddress"
                            type="email"
                            class="w-full h-full"
                            :class="{ 'p-invalid': emailError }"
                            :placeholder="$t('Email address')"
                            @input="emailError = ''"
                        />
                    </div>
                    <div style="min-width: 140px">
                        <Select
                            v-model="budgetEmailLocale"
                            :options="localeOptions"
                            optionLabel="label"
                            optionValue="value"
                            class="w-full h-full"
                        />
                    </div>
                </div>
                <small v-if="emailError" class="text-red-500">{{ emailError }}</small>
            </div>

            <ClientEmailMessagePanel
                email-type="budget"
                :client-locale="selectedClient?.client?.locale || 'en'"
                :show-locale="false"
                @change="payload => budgetEmailOptions = payload"
            />
        </div>
        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="budgetEmailDialogVisible = false" />
            <Button :label="$t('Send')" icon="pi pi-send" @click="sendBudgetEmail" :loading="budgetProcessing" />
        </template>
    </Dialog>

    <!-- Price Change Warning Banner -->
    <div v-if="hasAnyPriceChange" class="mb-4 p-4 bg-orange-50 border border-orange-300 rounded-lg flex items-start justify-between gap-3">
        <div class="flex items-start gap-3">
            <i class="pi pi-exclamation-triangle text-orange-500 text-xl mt-0.5"></i>
            <div>
                <div class="font-semibold text-orange-700">{{ $t('Price changes detected') }}</div>
                <div class="text-sm text-orange-600 mt-1">{{ $t('One or more sites have updated prices since this budget was created. Review the highlighted rows before saving or importing.') }}</div>
            </div>
        </div>
        <Button
            :label="$t('Update Price')"
            icon="pi pi-sync"
            severity="warn"
            size="small"
            class="shrink-0"
            @click="applyAllCurrentPrices"
        />
    </div>

    <!-- Save Changes Preview Dialog -->
    <BudgetReviewChangesDialog
        v-model:visible="savePreviewDialogVisible"
        :changesPreview="changesPreview"
        :loading="form.processing"
        @confirm="confirmSave"
    />

    <!-- Main Form -->
    <div class="grid">
        <div class="col-12">
            <Card>
                <template #title>
                    <div class="flex items-center justify-between mb-6">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-file-edit text-2xl"></i>
                            <span>{{ editBudget ? $t('Edit Budget') : $t('New Budget') }}</span>
                        </div>
                        <Button
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            text
                            rounded
                            v-tooltip.top="$t('Back')"
                            @click="goBack"
                        />
                    </div>
                </template>

                <template #content>
                    <form @submit.prevent="submit">
                        <div class="grid gap-10">
                            <!-- Client (optional for budgets) -->
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label class="block text-sm font-medium text-surface-700 mb-2">
                                        {{ $t('Client') }}
                                    </label>
                                    <AutoComplete
                                        v-model="selectedClient"
                                        :suggestions="filteredClients"
                                        @complete="searchClients"
                                        @item-select="onClientSelect"
                                        @clear="onClientClear"
                                        optionLabel="label"
                                        :placeholder="$t('Search for a client...')"
                                        class="w-full"
                                        :minLength="1"
                                        :dropdown="true"
                                        :forceSelection="false"
                                    />
                                </div>
                            </div>

                            <!-- Sites Table and Summary -->
                            <div class="col-12">
                                <div class="field">
                                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
                                        <!-- Sites Table -->
                                        <div class="lg:col-span-3">
                                            <Card>
                                                <template #title>
                                                    <div class="flex items-center justify-between">
                                                        <span>{{ $t('Budget Sites') }}</span>
                                                        <span class="text-sm font-normal text-surface-600">{{ form.sites?.length || 0 }} {{ (form.sites?.length || 0) === 1 ? $t('site') : $t('sites') }}</span>
                                                    </div>
                                                </template>
                                                <template #content>
                                                    <DataTable
                                                        :value="form.sites"
                                                        responsiveLayout="scroll"
                                                        class="p-datatable-sm"
                                                    >
                                                        <template #empty>
                                                            <div class="text-center p-4">
                                                                {{ $t('No sites added to this budget yet.') }}
                                                            </div>
                                                        </template>

                                                        <Column :header="$t('Actions')" style="width: 100px">
                                                            <template #body="slotProps">
                                                                <div class="flex gap-2">
                                                                    <Button
                                                                        icon="pi pi-trash"
                                                                        severity="danger"
                                                                        text
                                                                        rounded
                                                                        size="small"
                                                                        @click="remove(slotProps.index)"
                                                                    />
                                                                    <Button
                                                                        icon="pi pi-plus-circle"
                                                                        severity="info"
                                                                        text
                                                                        rounded
                                                                        size="small"
                                                                        @click="add(slotProps.data, slotProps.index)"
                                                                    />
                                                                </div>
                                                            </template>
                                                        </Column>

                                                        <Column field="url" :header="$t('Portal')" style="min-width: 160px; max-width: 220px;">
                                                            <template #body="slotProps">
                                                                <div class="flex items-center gap-2 min-w-0">
                                                                    <Tag v-if="slotProps.data.is_manual" severity="warn" value="Manual" class="mr-1 flex-shrink-0" />
                                                                    <span class="text-sm truncate" :title="slotProps.data.url || slotProps.data.manual_url">{{ slotProps.data.url || slotProps.data.manual_url }}</span>
                                                                    <a
                                                                        v-if="slotProps.data.id"
                                                                        :href="route('sites.edit', slotProps.data.id)"
                                                                        target="_blank"
                                                                        class="text-blue-500 flex-shrink-0"
                                                                    >
                                                                        <i class="pi pi-external-link text-xs"></i>
                                                                    </a>
                                                                </div>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Seller')" style="min-width: 180px">
                                                            <template #body="slotProps">
                                                                <Select
                                                                    v-if="(() => {
                                                                        const unique = new Set();
                                                                        (slotProps.data.types || []).forEach(t => { if (t.seller_id) unique.add(t.seller_id); });
                                                                        return unique.size > 1;
                                                                    })()"
                                                                    v-model="slotProps.data.seller_id"
                                                                    :options="(() => {
                                                                        const unique = new Map();
                                                                        (slotProps.data.types || []).forEach(t => {
                                                                            if (t.seller_id && !unique.has(t.seller_id)) {
                                                                                unique.set(t.seller_id, { label: t.seller?.name || 'Seller #' + t.seller_id, value: t.seller_id });
                                                                            }
                                                                        });
                                                                        return Array.from(unique.values());
                                                                    })()"
                                                                    optionLabel="label"
                                                                    optionValue="value"
                                                                    :placeholder="$t('Select seller')"
                                                                    class="w-full"
                                                                    @change="(() => {
                                                                        const site = form.sites[slotProps.index];
                                                                        const selectedType = site.types?.find(t => t.seller_id === site.seller_id);
                                                                        if (selectedType) {
                                                                            site.seller = selectedType.seller;
                                                                            site.type_id = selectedType.id;
                                                                            onTypeChange(slotProps.index);
                                                                            site.priceChanged = false;
                                                                            site.costChanged = false;
                                                                            site.costUp = false;
                                                                        }
                                                                    })()"
                                                                >
                                                                    <template #option="optionProps">
                                                                        <div class="flex items-center gap-1 w-full">
                                                                            <span>{{ optionProps.option.label }}</span>
                                                                            <i
                                                                                v-if="getSellerPriceIndicator(slotProps.data.types, optionProps.option.value) === 'lowest'"
                                                                                class="pi pi-arrow-down text-green-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Lowest price among sellers')"
                                                                            ></i>
                                                                            <i
                                                                                v-else-if="getSellerPriceIndicator(slotProps.data.types, optionProps.option.value) === 'highest'"
                                                                                class="pi pi-arrow-up text-red-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Highest price among sellers')"
                                                                            ></i>
                                                                        </div>
                                                                    </template>
                                                                    <template #value="valueProps">
                                                                        <div v-if="valueProps.value" class="flex items-center gap-1">
                                                                            <span>{{ (() => {
                                                                                const opt = slotProps.data.types?.find(t => t.seller_id === valueProps.value);
                                                                                return opt?.seller?.name || 'Seller #' + valueProps.value;
                                                                            })() }}</span>
                                                                            <i
                                                                                v-if="getSellerPriceIndicator(slotProps.data.types, valueProps.value) === 'lowest'"
                                                                                class="pi pi-arrow-down text-green-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Lowest price among sellers')"
                                                                            ></i>
                                                                            <i
                                                                                v-else-if="getSellerPriceIndicator(slotProps.data.types, valueProps.value) === 'highest'"
                                                                                class="pi pi-arrow-up text-red-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Highest price among sellers')"
                                                                            ></i>
                                                                        </div>
                                                                        <span v-else>{{ $t('Select seller') }}</span>
                                                                    </template>
                                                                </Select>
                                                                <span v-else class="text-sm text-surface-700">
                                                                    {{ slotProps.data.seller?.name || '-' }}
                                                                </span>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Type')" style="min-width: 200px">
                                                            <template #body="slotProps">
                                                                <div v-if="slotProps.data.is_manual" class="text-surface-500 italic">
                                                                    {{ $t('Manual item') }}
                                                                </div>
                                                                <template v-else>
                                                                    <Select
                                                                        v-model="slotProps.data.type_id"
                                                                        :options="getNichesForSeller(slotProps.data.types, slotProps.data.seller_id)"
                                                                        optionLabel="name"
                                                                        optionValue="id"
                                                                        :placeholder="$t('Choose a type')"
                                                                        class="w-full"
                                                                        @change="onTypeChange(slotProps.index)"
                                                                    >
                                                                        <template #option="optionProps">
                                                                            <div class="flex items-center justify-between w-full">
                                                                                <div class="flex items-center gap-1">
                                                                                    <span>{{ optionProps.option.name }}</span>
                                                                                    <i
                                                                                        v-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), optionProps.option.id) === 'lowest'"
                                                                                        class="pi pi-arrow-down text-green-600"
                                                                                        style="font-size: 0.65rem"
                                                                                        v-tooltip.top="$t('Lowest price among niches')"
                                                                                    ></i>
                                                                                    <i
                                                                                        v-else-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), optionProps.option.id) === 'highest'"
                                                                                        class="pi pi-arrow-up text-red-600"
                                                                                        style="font-size: 0.65rem"
                                                                                        v-tooltip.top="$t('Highest price among niches')"
                                                                                    ></i>
                                                                                </div>
                                                                                <span class="font-semibold text-primary-600 ml-2">
                                                                                    {{ formatConvertedPrice(optionProps.option.converted_price, optionProps.option.converted_coin) }}
                                                                                </span>
                                                                            </div>
                                                                        </template>
                                                                        <template #value="valueProps">
                                                                            <div v-if="valueProps.value" class="flex items-center gap-1">
                                                                                <span>{{ (() => {
                                                                                    const opt = slotProps.data.types?.find(t => t.id === valueProps.value);
                                                                                    return opt?.name || '';
                                                                                })() }}</span>
                                                                                <i
                                                                                    v-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), valueProps.value) === 'lowest'"
                                                                                    class="pi pi-arrow-down text-green-600"
                                                                                    style="font-size: 0.65rem"
                                                                                    v-tooltip.top="$t('Lowest price among niches')"
                                                                                ></i>
                                                                                <i
                                                                                    v-else-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), valueProps.value) === 'highest'"
                                                                                    class="pi pi-arrow-up text-red-600"
                                                                                    style="font-size: 0.65rem"
                                                                                    v-tooltip.top="$t('Highest price among niches')"
                                                                                ></i>
                                                                            </div>
                                                                            <span v-else>{{ $t('Choose a type') }}</span>
                                                                        </template>
                                                                    </Select>
                                                                    <small v-if="!slotProps.data.type_id" class="text-red-500">{{ $t('Required') }}</small>
                                                                </template>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Link Type')" style="min-width: 120px">
                                                            <template #body="slotProps">
                                                                <div class="relative inline-flex items-center justify-center">
                                                                    <div class="flex items-center gap-1 px-3 py-2 bg-surface-50 border border-surface-200 rounded-md text-surface-900 text-center">
                                                                        {{ formatLinkType(slotProps.data.link_type) }}{{ slotProps.data.type_observation ? '*' : '' }}
                                                                        <i
                                                                            v-if="slotProps.data.type_observation"
                                                                            class="pi pi-info-circle text-blue-500 cursor-pointer"
                                                                            v-tooltip.top="slotProps.data.type_observation"
                                                                            style="font-size: 0.7rem;"
                                                                        ></i>
                                                                    </div>
                                                                </div>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Purchase Value')">
                                                            <template #body="slotProps">
                                                                <div v-if="slotProps.data.cost" class="flex flex-col gap-1">
                                                                    <span :class="slotProps.data.costChanged ? 'line-through text-surface-400 whitespace-nowrap' : 'whitespace-nowrap'">
                                                                        {{ formatConvertedPrice(slotProps.data.cost, slotProps.data.cost_coin || 'USD') }}
                                                                    </span>
                                                                    <div v-if="slotProps.data.costChanged" class="flex items-center gap-1">
                                                                        <Tag severity="warn" class="text-xs">
                                                                            <i :class="slotProps.data.costUp ? 'pi pi-arrow-up mr-1' : 'pi pi-arrow-down mr-1'" style="font-size:0.65rem"></i>
                                                                            {{ formatConvertedPrice(slotProps.data.currentCost, slotProps.data.currentCostCoin) }}
                                                                        </Tag>
                                                                    </div>
                                                                </div>
                                                                <span v-else>-</span>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Sale Value')">
                                                            <template #body="slotProps">
                                                                <div v-if="slotProps.data.sale" class="flex flex-col gap-1">
                                                                    <div class="flex items-center gap-1 whitespace-nowrap">
                                                                        <span :class="slotProps.data.priceChanged ? 'line-through text-surface-400' : ''">
                                                                            {{ formatConvertedPrice(slotProps.data.sale, slotProps.data.sale_coin || 'USD') }}
                                                                        </span>
                                                                        <Button
                                                                            icon="pi pi-pencil"
                                                                            text
                                                                            rounded
                                                                            size="small"
                                                                            class="ml-1"
                                                                            @click="edit('sale', slotProps.index, slotProps.data)"
                                                                        />
                                                                    </div>
                                                                    <div v-if="slotProps.data.priceChanged" class="flex items-center gap-1">
                                                                        <Tag severity="warn" class="text-xs">
                                                                            <i :class="slotProps.data.costUp ? 'pi pi-arrow-up mr-1' : 'pi pi-arrow-down mr-1'" style="font-size:0.65rem"></i>
                                                                            {{ formatConvertedPrice(slotProps.data.currentSale, slotProps.data.currentSaleCoin) }}
                                                                        </Tag>
                                                                    </div>
                                                                </div>
                                                                <span v-else>-</span>
                                                            </template>
                                                        </Column>
                                                    </DataTable>

                                                    <Message v-if="linkTypeNotes.length" severity="info" :closable="false" class="mt-3">
                                                        <div v-for="site in linkTypeNotes" :key="site.id || site.url" class="text-sm">
                                                            * {{ site.url }}: {{ site.type_observation }}
                                                        </div>
                                                    </Message>

                                                    <Button
                                                        :label="$t('Add sites')"
                                                        icon="pi pi-plus"
                                                        class="mt-4"
                                                        @click="addSites"
                                                    />

                                                    <BudgetSuggestionsPanel
                                                        :best-sellers="bestSellers"
                                                        :client-history="clientHistory"
                                                        :client-history-loading="clientHistoryLoading"
                                                        :recommended="recommended"
                                                        :client-selected="!!form.client_id"
                                                        :existing-site-ids="form.sites.map(s => s.id).filter(Boolean)"
                                                        @add="addSuggestion"
                                                        @add-all="addAllSuggestions"
                                                    />
                                                </template>
                                            </Card>
                                        </div>

                                        <!-- Budget Summary -->
                                        <div class="lg:col-span-1">
                                            <Card>
                                                <template #title>
                                                    {{ $t('Budget Summary') }}
                                                </template>
                                                <template #content>
                                                    <div class="space-y-4">
                                                        <div class="flex justify-between items-center">
                                                            <span class="text-surface-700">{{ $t('Total Sites') }}:</span>
                                                            <span class="font-semibold text-lg">{{ form.sites.length }}</span>
                                                        </div>

                                                        <Divider />

                                                        <div class="flex justify-between items-center">
                                                            <span class="text-surface-700">{{ $t('Purchase Value') }}:</span>
                                                            <span class="font-semibold whitespace-nowrap">{{ costTotal || '--' }}</span>
                                                        </div>

                                                        <div class="flex justify-between items-center">
                                                            <span class="text-surface-700">{{ $t('Sale Value') }}:</span>
                                                            <span class="font-semibold text-primary-600 whitespace-nowrap">{{ saleTotal }}</span>
                                                        </div>

                                                        <Divider />

                                                        <!-- Preview / PDF / Import to Order -->
                                                        <div v-if="isFormValid" class="flex flex-wrap gap-2">
                                                            <Button
                                                                :label="$t('Preview')"
                                                                icon="pi pi-eye"
                                                                severity="secondary"
                                                                size="small"
                                                                :loading="budgetProcessing"
                                                                @click.prevent="previewBudgetPdf"
                                                            />
                                                            <Button
                                                                :label="$t('PDF')"
                                                                icon="pi pi-download"
                                                                severity="info"
                                                                size="small"
                                                                :loading="budgetProcessing"
                                                                @click.prevent="downloadBudgetPdf"
                                                            />
                                                            <Button
                                                                :label="$t('Import to Order')"
                                                                icon="pi pi-shopping-cart"
                                                                severity="warn"
                                                                size="small"
                                                                @click.prevent="importBudgetToOrder"
                                                            />
                                                        </div>

                                                        <div v-if="form.sites.length > 0 && !isFormValid" class="text-sm">
                                                            <div class="text-orange-600">
                                                                <i class="pi pi-exclamation-triangle mr-1"></i>
                                                                {{ $t('Please select a type for all sites') }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Card>

                                            <!-- Save / Send by Email - outside the card -->
                                            <div class="flex flex-wrap gap-2 mt-10 justify-end">
                                                <Button
                                                    :label="$t('Save')"
                                                    icon="pi pi-save"
                                                    severity="primary"
                                                    :loading="form.processing"
                                                    :disabled="form.processing || !isFormValid || (editBudget && !isDirtyEdit)"
                                                    @click.prevent="handleSave"
                                                />
                                                <Button
                                                    v-if="isFormValid"
                                                    :label="$t('Send by Email')"
                                                    icon="pi pi-envelope"
                                                    severity="success"
                                                    :loading="budgetProcessing"
                                                    @click.prevent="budgetEmailDialogVisible = true"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </template>
            </Card>
        </div>
    </div>

    <!-- Quick Offer Modal -->
    <QuickOfferModal
        v-model:visible="quickOfferModalVisible"
        :initial-url="quickOfferInitialUrl"
        :site-exists="quickOfferSiteExists"
        :site-id="quickOfferSiteId"
        :site-da="quickOfferSiteDa"
        :site-dr="quickOfferSiteDr"
        :currency="currentCurrency"
        @created="handleQuickOfferCreated"
    />

</template>

<style scoped>
.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
